import {
  ArrowRight,
  CalendarDays,
  CircleAlert,
  Megaphone,
} from "lucide-react";
import { Link } from "react-router-dom";
import { DepartmentIcon } from "../components/ContentIcons";
import { NewsList } from "../components/NewsList";
import { UpcomingList } from "../components/UpcomingList";
import { useCalendarEvents } from "../hooks/useCalendarEvents";
import {
  departments,
  formatShortDate,
  getActiveNews,
  getDepartmentName,
  getImportantNews,
} from "../lib/content";

export function HomePage() {
  const important = getImportantNews().slice(0, 3);
  const feature = important[0];
  const secondary = important.slice(1);
  const latest = getActiveNews().sort((a, b) => Date.parse(b.publishedAt) - Date.parse(a.publishedAt)).slice(0, 4);
  const { events: upcoming, state: calendarState } = useCalendarEvents();
  const calendarMonth = new Intl.DateTimeFormat("en-US", { month: "long", year: "numeric" }).format(new Date());

  return (
    <>
      <section className="home-intro">
        <div className="page-width">
          <span className="home-date">{new Intl.DateTimeFormat("en-US", { weekday: "long", month: "long", day: "numeric", timeZone: "America/Los_Angeles" }).format(new Date())}</span>
          <h1>Your staff hub</h1>
          <p>What matters now. What’s changed. What’s coming up.</p>
        </div>
      </section>

      <section className="section page-width important-section" aria-labelledby="important-heading">
        <div className="section-heading-row">
          <div>
            <span className="section-symbol"><Megaphone aria-hidden="true" size={20} /></span>
            <h2 id="important-heading">Important updates</h2>
          </div>
          <Link className="text-link" to="/news?priority=important">
            View important updates <ArrowRight aria-hidden="true" size={18} />
          </Link>
        </div>
        {feature ? (
          <div className={`important-layout${secondary.length ? "" : " important-layout--single"}`}>
            <article className="featured-news">
              <div className="featured-news__icon"><Megaphone aria-hidden="true" size={45} /></div>
              <div className="featured-news__copy">
                {feature.actionNeeded ? (
                  <span className="action-label action-label--large">
                    <CircleAlert aria-hidden="true" size={16} /> Action needed
                  </span>
                ) : null}
                <h3>{feature.title}</h3>
                <p>{feature.summary}</p>
                <div className="feature-meta">
                  <span>{getDepartmentName(feature.department)}</span>
                  {feature.effectiveAt ? <span>Effective {formatShortDate(feature.effectiveAt)}</span> : null}
                </div>
                <Link className="button button--primary" to={`/news/${feature.slug}`}>
                  Read the update <ArrowRight aria-hidden="true" size={19} />
                </Link>
              </div>
            </article>
            {secondary.length ? <div className="important-secondary">
              {secondary.map((item) => (
                <Link key={item.id} to={`/news/${item.slug}`}>
                  <span className={`content-icon accent-${item.department}`}>
                    <DepartmentIcon id={item.department} size={28} />
                  </span>
                  <span>
                    <strong>{item.title}</strong>
                    <small>
                      {getDepartmentName(item.department)}
                      {item.effectiveAt ? ` · Effective ${formatShortDate(item.effectiveAt)}` : ""}
                    </small>
                  </span>
                  <ArrowRight aria-hidden="true" size={20} />
                </Link>
              ))}
            </div> : null}
          </div>
        ) : (
          <div className="home-clear-state">
            <p>No important notices right now. Check the latest updates and upcoming events below.</p>
          </div>
        )}
      </section>

      <nav className="page-width staff-quick-actions" aria-label="Quick staff actions">
        <a href="https://bionboarding.netlify.app" target="_blank" rel="noopener noreferrer">Onboarding &amp; Training <ArrowRight aria-hidden="true" size={18} /><span className="sr-only"> (opens in a new tab)</span></a>
        <Link to="/links">Bethlehem Inn links <ArrowRight aria-hidden="true" size={18} /></Link>
        <Link to="/upcoming">Check the calendar <CalendarDays aria-hidden="true" size={18} /></Link>
      </nav>

      <section className="section-band">
        <div className="page-width home-feed-grid">
          <section aria-labelledby="latest-heading">
            <div className="section-heading-row">
              <h2 id="latest-heading">Latest updates</h2>
              <Link className="text-link home-latest-link" to="/news">
                All updates <ArrowRight aria-hidden="true" size={18} />
              </Link>
            </div>
            <NewsList compact items={latest} />
            <Link className="text-link section-link mobile-only" to="/news">
              All updates <ArrowRight aria-hidden="true" size={18} />
            </Link>
          </section>
          <section aria-labelledby="upcoming-heading">
            <div className="section-heading-row">
              <h2 id="upcoming-heading">Upcoming</h2>
              <CalendarDays aria-hidden="true" className="heading-icon" />
            </div>
            <p className="feed-intro">{calendarMonth} · Approved staff events and organizational dates.</p>
            {calendarState === "partial" ? <p className="feed-notice" role="status">Some calendar information is temporarily unavailable.</p> : null}
            {calendarState === "unavailable" ? <p className="feed-notice" role="status">Google Calendar is temporarily unavailable. Any available Staff Admin events are shown below.</p> : null}
            {calendarState !== "unavailable" || upcoming.length > 0 ? <UpcomingList items={upcoming} limit={3} loading={calendarState === "loading" && !upcoming.length} /> : null}
            <Link className="text-link section-link" to="/upcoming">
              View Calendar <ArrowRight aria-hidden="true" size={18} />
            </Link>
          </section>
        </div>
      </section>

      <section className="department-band" aria-labelledby="department-heading">
        <div className="page-width">
          <div className="section-heading-row">
            <h2 id="department-heading">Explore departments</h2>
            <Link className="text-link" to="/departments">
              All departments <ArrowRight aria-hidden="true" size={18} />
            </Link>
          </div>
          <nav aria-label="Department spaces" className="department-rail">
            {departments.map((department) => (
              <Link key={department.id} to={`/departments/${department.id}`}>
                <DepartmentIcon id={department.id} size={25} />
                <span>{department.name}</span>
                <ArrowRight aria-hidden="true" size={18} />
              </Link>
            ))}
          </nav>
        </div>
      </section>

    </>
  );
}
